export async function resolveSignedInTenantUser(candidateUser, {
        supabaseClient,
        provisioningInFlightRef,
        isCurrent = () => true,
        setUser,
        setMembershipOrgId,
        setProvisioningInFlight,
        setTenantName,
        setNewMembershipCreatedThisSession,
        setAuthMode,
        setAuthMessage = () => {},
        onVerificationRequired,
        onExpiredInvitation,
        alertFn = window.alert,
        storage = localStorage,
        // BL-408 G2: called only when the person's own membership row is not visible (two or more companies and none
        // chosen yet hides every row). Resolves to an org id once the server has entered a company, or null when the
        // person must choose first (the caller shows "Choose a company"). One-company people never reach it.
        resolveMultipleCompanies = null,
}) {
        if (!candidateUser) {
                    setUser(null);
                    setMembershipOrgId(null);
                    return;
        }

    if (provisioningInFlightRef.current) {
                // A resolution for this session is already in flight (see CRMApp.jsx's dedupe by
                // candidate user id for why this can still be reached) -- don't optimistically
                // mark it signed-in before that in-flight call's own gate has a verdict. It will
                // set the definitive state when it finishes.
                return;
    }

    const isConfirmed = Boolean(candidateUser?.email_confirmed_at);
        if (!isConfirmed) {
                    setUser(candidateUser);
                    return;
        }

    provisioningInFlightRef.current = true;
        setProvisioningInFlight(true);
        let { data: existingMembership, error: membershipError } = await supabaseClient
            .from('org_members')
            .select('org_id')
            .eq('user_id', candidateUser.id)
            .maybeSingle();
    if (!isCurrent()) return;

    if (resolveMultipleCompanies && (membershipError || !existingMembership)) {
        const outcome = await resolveMultipleCompanies(candidateUser);
        if (!isCurrent()) return;
        if ((outcome?.kind === 'entered' || outcome?.kind === 'active') && outcome.orgId) {
            existingMembership = { org_id: outcome.orgId };
            membershipError = null;
        } else if (outcome && outcome.kind !== 'single') {
            provisioningInFlightRef.current = false;
            setProvisioningInFlight(false);
            setMembershipOrgId(null);
            setUser(null);
            return;
        }
    }

    if (membershipError) {
                provisioningInFlightRef.current = false;
                setProvisioningInFlight(false);
            setMembershipOrgId(null);
            setUser(null);
            setAuthMode('login');
            setAuthMessage("We couldn't verify access right now. Check your connection and try again.");
            setTimeout(() => {
                if (isCurrent()) supabaseClient.auth.signOut();
            }, 0);
            return;
    }

    let { data: appAccess, error: appAccessError } = await supabaseClient.rpc('bl391_app_access');
    if (!isCurrent()) return;
    if (!appAccessError && appAccess?.invitation_state === 'expired') {
        provisioningInFlightRef.current = false;
        setProvisioningInFlight(false);
        setMembershipOrgId(null);
        setUser(null);
        onExpiredInvitation?.(candidateUser);
        return;
    }
    if (!appAccessError && appAccess?.invitation_state === 'pending') {
        const { error: inviteError } = await supabaseClient.rpc('accept_org_invite');
        if (!isCurrent()) return;
        if (inviteError) {
            provisioningInFlightRef.current = false;
            setProvisioningInFlight(false);
            setMembershipOrgId(null); setUser(null);
            if (inviteError.code === '42501'
                && inviteError.message === 'Verify your code and create your password before accepting the invite'
                && onVerificationRequired) {
                onVerificationRequired(candidateUser);
            } else if (inviteError.code === 'IA400' && onExpiredInvitation) {
                onExpiredInvitation(candidateUser);
            } else {
                setAuthMode('login');
                setAuthMessage('Unable to accept your organization invitation: ' + inviteError.message);
            }
            return;
        }
        let membership = await supabaseClient.from('org_members')
            .select('org_id').eq('user_id', candidateUser.id).maybeSingle();
        if (!isCurrent()) return;
        // BL-408 G3b: accepting an invitation into a SECOND company leaves two companies and none chosen, which hides every
        // membership row. Hand over to the same multi-company landing as above (door company, or "Choose a company").
        if (resolveMultipleCompanies && !membership.error && !membership.data) {
            const outcome = await resolveMultipleCompanies(candidateUser);
            if (!isCurrent()) return;
            if ((outcome?.kind === 'entered' || outcome?.kind === 'active') && outcome.orgId) {
                membership = { data: { org_id: outcome.orgId }, error: null };
            } else if (outcome && outcome.kind !== 'single') {
                provisioningInFlightRef.current = false;
                setProvisioningInFlight(false);
                setMembershipOrgId(null);
                setUser(null);
                return;
            }
        }
        if (membership.error || !membership.data) {
            provisioningInFlightRef.current = false;
            setProvisioningInFlight(false);
            setMembershipOrgId(null); setUser(null); setAuthMode('login');
            setAuthMessage('Unable to verify your invitation membership: '
                + (membership.error?.message || 'No team membership was created.'));
            return;
        }
        existingMembership = membership.data;
        ({ data: appAccess, error: appAccessError } = await supabaseClient.rpc('bl391_app_access'));
        if (!isCurrent()) return;
    }
    if (appAccessError) {
        provisioningInFlightRef.current = false;
        setProvisioningInFlight(false);
        setMembershipOrgId(null);
        setUser(null);
        setAuthMode('login');
        setAuthMessage("We couldn't verify access right now. Check your connection and try again.");
        setTimeout(() => {
            if (isCurrent()) supabaseClient.auth.signOut();
        }, 0);
        return;
    }
    if (appAccess?.command_center_enabled === false && appAccess?.driver_app_enabled === true) {
        provisioningInFlightRef.current = false;
        setProvisioningInFlight(false);
        setMembershipOrgId(null);
        setNewMembershipCreatedThisSession(false);
        setAuthMode('driver-only');
        setUser({ ...candidateUser, app_access: appAccess });
        return;
    }
    if (existingMembership && appAccess?.command_center_enabled === false) {
        provisioningInFlightRef.current = false;
        setProvisioningInFlight(false);
        setMembershipOrgId(null);
        setUser(null);
        setAuthMode('login');
        setAuthMessage('Central Command access has been revoked. Contact your organization administrator.');
        return;
    }

    if (!existingMembership && !(candidateUser?.user_metadata?.org_name && candidateUser?.user_metadata?.owner_name)) {
        const { error: inviteError } = await supabaseClient.rpc('accept_org_invite');
        if (!isCurrent()) return;
        if (inviteError) {
            provisioningInFlightRef.current = false;
            setProvisioningInFlight(false);
            setMembershipOrgId(null);
            setUser(null);
            if (inviteError.code === '42501'
                && inviteError.message === 'Verify your code and create your password before accepting the invite'
                && onVerificationRequired) {
                setAuthMessage('');
                onVerificationRequired(candidateUser);
                return;
            }
            if (inviteError.code === 'IA400' && onExpiredInvitation) {
                onExpiredInvitation(candidateUser);
                return;
            }
            setAuthMode('login');
            setAuthMessage('Unable to accept your organization invitation: ' + inviteError.message);
            return;
        }
        const { data: invitedMembership, error: invitedMembershipError } = await supabaseClient
            .from('org_members').select('org_id').eq('user_id', candidateUser.id).maybeSingle();
        if (!isCurrent()) return;
        if (invitedMembershipError) {
            provisioningInFlightRef.current = false;
            setProvisioningInFlight(false);
            setMembershipOrgId(null);
            setUser(null);
            setAuthMode('login');
            setAuthMessage('Unable to verify your invitation membership: ' + invitedMembershipError.message);
            return;
        }
        existingMembership = invitedMembership;
    }

    setMembershipOrgId(existingMembership?.org_id || null);
        const orgName = candidateUser?.user_metadata?.org_name;
        const ownerName = candidateUser?.user_metadata?.owner_name;
        if (orgName) {
                    setTenantName(orgName);
                    storage.setItem('intelAgentTenantName', orgName);
        }

    if (!existingMembership && orgName && ownerName) {
                const { data: provisionedOrgId, error: provisionError } = await supabaseClient.rpc('provision_new_tenant', {
                                p_org_name: orgName,
                                p_owner_user_id: candidateUser.id
                });
                if (!isCurrent()) return;
                if (provisionError) {
                                provisioningInFlightRef.current = false;
                                setProvisioningInFlight(false);
                                alertFn('Tenant provisioning failed: ' + provisionError.message);
                                setUser(null);
                                setAuthMode('login');
                                return;
                }
                // provision_new_tenant RETURNS the new org id; without this, membershipOrgId would stay
            // null for the rest of this session (the lookup above ran before the org existed), and
            // both Gate 4's OnboardingWizard and Gate 5's FirstOwnerSetupWizard need a real orgId.
            if (provisionedOrgId) setMembershipOrgId(provisionedOrgId);
                setNewMembershipCreatedThisSession(true);
    } else if (!existingMembership) {
                // No org_members row alone doesn't make this an intruder -- platform super-admins
            // operate across every tenant by design and are never given a membership row in any
            // one of them. Check platform-admin status before treating a missing row as "unknown
            // identity" and blocking the sign-in.
                const { data: isPlatformAdmin, error: platformAdminError } = await supabaseClient.rpc('is_platform_super_admin');
                if (!isCurrent()) return;
                if (!platformAdminError && isPlatformAdmin) {
                                // Recognized platform staff signing in with no tenant membership -- let them
                // through like anyone else with a resolved identity below; nothing to block.
                                setNewMembershipCreatedThisSession(false);
                } else {
                // No tenant membership AND no signup metadata (org_name/owner_name only ever come from
                                        // the email/password signup form) -- this is an SSO sign-in (or any other auth path)
                                        // with no invite tied to this identity yet. Previously this fell through to
                                        // setUser(candidateUser) below and granted a live session with membershipOrgId = null,
                                        // landing the person straight on the dashboard. Block it instead -- sign the session
                                        // back out and route to the no-tenant screen so they can create a new organization or
                                        // go get invited by an existing admin.
                                                                provisioningInFlightRef.current = false;
                                        setProvisioningInFlight(false);
                                        // IMPORTANT: this resolver runs inside CRMApp.jsx's onAuthStateChange callback (it
                                        // is awaited from that handler). supabase-js has a documented bug where calling ANY
                                        // supabaseClient.auth.* method and awaiting it directly inside that callback's call
                                        // stack deadlocks forever -- the callback runs while gotrue-js holds its own internal
                                        // session lock (a navigator.locks lock named "lock:sb-<project-ref>-auth-token"), and
                                        // signOut() needs that very lock to clear the session, so its lock request just sits
                                        // pending until the tab is closed. The app is then stuck on the "Loading..." screen
                                        // permanently -- in every browser/profile, incognito included -- because the callback
                                        // that would set loading=false never returns.
                                        // See: https://supabase.com/docs/guides/troubleshooting/why-is-my-supabase-api-call-not-returning-PGzXw0
                                        // Fix: defer the signOut() call to a fresh macrotask via setTimeout(0) so it fires
                                        // AFTER this callback (and resolveSignedInTenantUser's own promise) has returned and
                                        // released the lock. We intentionally do not await it -- the local state below is
                                        // what actually routes the user to the no-tenant screen; signOut() just clears the
                                        // stale session token afterward.
                                        setTimeout(() => {
                                                                                if (isCurrent()) supabaseClient.auth.signOut();
                                        }, 0);
                setMembershipOrgId(null);
                setNewMembershipCreatedThisSession(false);
                const blockedEmail = candidateUser?.email || '';
                const noTenantMessage = blockedEmail ? `We couldn't find an organization for ${blockedEmail}.` : "We couldn't find an organization for that sign-in.";
                setAuthMessage(noTenantMessage);
                setAuthMode('no-tenant');
                setUser(null);
                return;
                }
    } else {
                setNewMembershipCreatedThisSession(false);
    }

    provisioningInFlightRef.current = false;
        setProvisioningInFlight(false);
        setUser(candidateUser);
}
